"use client";

import React from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
import { cn } from "@/core/lib/utils";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Heading } from "@/components/ui/Heading";
import ProductBreadcrumb from "./ProductBreadcrumb";
import { ProductSortSidebar } from "./ProductSort";
import AvailabilityFilterProduct from "./AvailabilityFilterProduct";

const CustomButton = loadThemeComponent("CustomButton");

interface Category {
  label: string;
  value: string;
}

interface CategoryNavProps {
  categories: Category[];
  activeSlug: string;
}

function CategoryNavComponent({ categories, activeSlug }: CategoryNavProps) {
  const { dictionary } = useDictionary();

  const allCategories = React.useMemo(
    () => [{ label: dictionary.ui.profile.all, value: "all" }, ...categories],
    [categories, dictionary]
  );

  const activeCategory = allCategories.find(c => c.value === activeSlug);

  return (
    <div className="mt-7 mb-5">
      <ProductBreadcrumb
        activeLabel={activeCategory?.label || ""}
        activeSlug={activeSlug}
      />

      {activeCategory && <Heading level={2}>{activeCategory.label}</Heading>}

      <div className="mt-7 border-b border-secondary-200 mb-6">
        <Swiper
          slidesPerView="auto"
          spaceBetween={8}
          freeMode
          className="!py-1"
        >
          {allCategories.map(({ label, value }) => {
            const isActive = activeSlug === value;

            return (
              <SwiperSlide key={value} className="!w-auto">
                <CustomButton
                  asChild
                  variant="transparent"
                  className={cn(
                    isActive &&
                    "text-secondary-100 hover:text-secondary-100 font-semibold"
                  )}
                >
                  <Link href={`/shop/category/products/${value}`}>
                    {label}
                  </Link>
                </CustomButton>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      <div className="flex justify-between">
        <ProductSortSidebar />
        <AvailabilityFilterProduct />
      </div>
    </div>
  );
}

export const CategoryNav = React.memo(CategoryNavComponent);
